
import { useState, useEffect, useRef, useCallback } from "react";
import "./RecordPlayerModal.css";
import { FiClock, FiPhone, FiPhoneOutgoing, FiVolume } from "react-icons/fi";
import {
    FiVolumeX,
    FiVolume1,
    FiVolume2
} from "react-icons/fi";
const BAR_COUNT = 60;
const WAVE_HEIGHTS = Array.from({ length: BAR_COUNT }, (_, i) =>
    Math.min(
        20 + Math.abs(Math.sin(i * 0.4 + Math.cos(i * 0.13) * 3)) * 62 + ((i * 7 + 13) % 19),
        90
    )
);

const fmt = (s) => {
    const sec = Math.round(s || 0);
    return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;
};

const isInterruptedPlayError = (err) =>
    err?.name === "AbortError" ||
    err?.message?.includes("play() request was interrupted");

export default function CallRecordingPlayerModal({
    isOpen,
    onClose,
    audioRef,
    audioUrl,
    callItem = null,
}) {
    // Derive all display values from the API item object
    const agentEmail = callItem?.agent_email || "";
    const callDate = callItem?.updated_at ? new Date(callItem.updated_at).toLocaleString() : "";
    const fromNumber = callItem?.called_from || "";
    const toNumber = callItem?.called_to || "";
    const callDuration = callItem?.call_duration ?? null;

    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(0.8);
    const [speed, setSpeed] = useState(1);
    const [isDragging, setIsDragging] = useState(false);
    const waveRef = useRef(null);
    const animRef = useRef(null);

    const initials =
        (agentEmail || "AG")
            .split("@")[0]
            .split(/[._-]/)
            .map((p) => p[0]?.toUpperCase() || "")
            .join("")
            .slice(0, 2) || "AG";

    // Duration comes purely from API — never from the audio element
    useEffect(() => {
        setDuration(callDuration ?? 0);
        setCurrentTime(0);
    }, [callItem]);

    // Sync audio element events into modal state
    useEffect(() => {
        const audio = audioRef?.current;
        if (!audio) return;

        // Always use API duration — never derive from audio element (stale/shared ref issue)
        setCurrentTime(0);
        setDuration(callDuration ?? 0);

        const onPlay = () => setIsPlaying(true);
        const onPause = () => setIsPlaying(false);
        const onEnded = () => { setIsPlaying(false); setCurrentTime(0); };

        audio.addEventListener("play", onPlay);
        audio.addEventListener("pause", onPause);
        audio.addEventListener("ended", onEnded);

        setIsPlaying(!audio.paused);

        return () => {
            audio.removeEventListener("play", onPlay);
            audio.removeEventListener("pause", onPause);
            audio.removeEventListener("ended", onEnded);
        };
    }, [audioRef, audioUrl, callDuration]);

    // rAF loop for smooth progress bar
    useEffect(() => {
        const audio = audioRef?.current;
        if (!audio || !isOpen) return;

        const tick = () => {
            if (!isDragging) setCurrentTime(audio.currentTime || 0);
            animRef.current = requestAnimationFrame(tick);
        };
        animRef.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(animRef.current);
    }, [isOpen, isDragging, audioRef]);

    // Reset speed+volume when a new recording is opened
    useEffect(() => {
        if (isOpen && audioRef?.current) {
            setSpeed(1);
            setVolume(0.8);
            audioRef.current.volume = 0.8;
            audioRef.current.playbackRate = 1;
        }
    }, [audioUrl, isOpen]);

    const togglePlay = async () => {
        const audio = audioRef?.current;
        if (!audio) return;
        if (isPlaying) {
            audio.pause();
            return;
        }

        try {
            await audio.play();
        } catch (err) {
            if (!isInterruptedPlayError(err)) {
                console.error("Error playing audio:", err);
            }
        }
    };

    const skip = (secs) => {
        const audio = audioRef?.current;
        if (!audio) return;
        audio.currentTime = Math.max(0, Math.min(duration, audio.currentTime + secs));
    };

    const handleSeek = (val) => {
        const t = parseFloat(val);
        setCurrentTime(t);
        if (audioRef?.current) audioRef.current.currentTime = t;
    };

    const handleVolume = (val) => {
        const v = parseFloat(val);
        setVolume(v);
        if (audioRef?.current) audioRef.current.volume = v;
    };

    const handleSpeed = (s) => {
        setSpeed(s);
        if (audioRef?.current) audioRef.current.playbackRate = s;
    };

    const handleWaveClick = useCallback(
        (e) => {
            if (!waveRef.current || !duration) return;
            const rect = waveRef.current.getBoundingClientRect();
            const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
            const t = pct * duration;
            setCurrentTime(t);
            if (audioRef?.current) audioRef.current.currentTime = t;
        },
        [duration, audioRef]
    );
    const volIcon =
        volume === 0 ? (
            <FiVolumeX size={18} />
        ) : volume < 0.25 ? (
            <FiVolume size={18} />
        ) : volume < 0.75 ? (
            <FiVolume1 size={18} />
        ) : (
            <FiVolume2 size={18} />
        );
    if (!isOpen) return null;

    const progress = duration > 0 ? currentTime / duration : 0;
    const playedBars = Math.round(progress * BAR_COUNT);
    const SPEEDS = [0.5, 1, 1.5, 2];

    return (
        <div
            className="recorder-overlay"
            
        >
            <div
                className="recorder-modal"
                role="dialog"
                aria-modal="true"
                aria-label="Call recording player"
            >
                {/* ── Header ── */}
                <div className="recorder-header">
                    <div className="recorder-header__left">

                        <div className="recorder-header__from-to">

                            <div className="recorder-header__item">
                                <FiPhoneOutgoing
                                    size={13}
                                    style={{ color: "#1976d2" }}
                                />


                                <span className="recorder-header__number">
                                    {fromNumber || "Unknown"}
                                </span>
                            </div>

                            <svg
                                className="recorder-header__arrow"
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#aaa"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <line x1="5" y1="12" x2="19" y2="12" />
                                <polyline points="12 5 19 12 12 19" />
                            </svg>

                            <div className="recorder-header__item">
                                <FiPhone
                                    size={13}
                                    style={{ color: "#2e7d32" }}
                                />

                                <span className="recorder-header__number">
                                    {toNumber || "Unknown"}
                                </span>
                            </div>

                        </div>
                    </div>
                    <button
                        className="recorder-header__close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        ✕
                    </button>
                </div>

                {/* ── Agent Info ── */}
                <div className="recorder-agent-row">
                    <div className="recorder-agent-row__avatar">{initials}</div>
                    <div className="recorder-agent-row__info">
                        <div className="recorder-agent-row__email">{agentEmail || "—"}</div>
                        <div className="recorder-agent-row__meta"> <FiClock size={11} className="record-clock1" /> {callDate}</div>

                        {duration > 0 && (
                            <div className="recorder-agent-row__meta">
                            <span className="recorder-agent-row__duration-badge">
                                <FiClock size={11} className="record-clock"/> {fmt(duration)}
                            </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Waveform ── */}
                <div
                    ref={waveRef}
                    className="recorder-wave-area"
                    onClick={handleWaveClick}
                    title="Click to seek"
                    role="slider"
                    aria-label="Audio seek"
                    aria-valuenow={Math.round(currentTime)}
                    aria-valuemin={0}
                    aria-valuemax={Math.round(duration)}
                >
                    <div className="recorder-wave-bars">
                        {WAVE_HEIGHTS.map((h, i) => (
                            <div
                                key={i}
                                className="recorder-wave-bar"
                                style={{
                                    height: `${h}%`,
                                    background:
                                        i < playedBars
                                            ? "#378ADD"
                                            : "rgba(135,147,161,0.28)",
                                }}
                            />
                        ))}
                    </div>
                    <div
                        className="recorder-playhead"
                        style={{ left: `calc(${progress * 100}% - 1px)` }}
                    />
                    <div
                        className="recorder-playhead-thumb"
                        style={{ left: `calc(${progress * 100}% - 7px)` }}
                    />
                </div>

                {/* ── Time Row ── */}
                <div className="recorder-time-row">
                    <span className="recorder-time-text">{fmt(currentTime)}</span>
                    <span className="recorder-time-text">{fmt(duration)}</span>
                </div>

                {/* ── Seek Slider ── */}
                <div className="recorder-seek-slider">
                    <input
                        type="range"
                        min={0}
                        max={duration || 100}
                        step={0.1}
                        value={currentTime}
                        onMouseDown={() => setIsDragging(true)}
                        onMouseUp={() => setIsDragging(false)}
                        onTouchStart={() => setIsDragging(true)}
                        onTouchEnd={() => setIsDragging(false)}
                        onChange={(e) => handleSeek(e.target.value)}
                        className="recorder-range"
                        aria-label="Seek"
                    />
                </div>

                {/* ── Controls ── */}
                <div className="recorder-controls">
                    <SkipBtn onClick={() => skip(-15)} label="Back 15s">
                        <svg
                            width="18" height="18" viewBox="0 0 24 24"
                            fill="none" stroke="currentColor" strokeWidth="2"
                            strokeLinecap="round" strokeLinejoin="round"
                        >
                            <polyline points="1 4 1 10 7 10" />
                            <path d="M3.51 15a9 9 0 1 0 .49-3.87" />
                        </svg>
                        <span className="recorder-skip-btn__label">15</span>
                    </SkipBtn>

                    <SkipBtn onClick={() => skip(-5)} label="Back 5s">
                        <svg
                            width="16" height="16" viewBox="0 0 24 24"
                            fill="none" stroke="currentColor" strokeWidth="2"
                            strokeLinecap="round" strokeLinejoin="round"
                        >
                            <polygon points="19 20 9 12 19 4 19 20" />
                            <line x1="5" y1="19" x2="5" y2="5" />
                        </svg>
                    </SkipBtn>

                    <button
                        className={`recorder-play-btn${isPlaying ? " recorder-play-btn--playing" : ""}`}
                        onClick={togglePlay}
                        aria-label={isPlaying ? "Pause" : "Play"}
                    >
                        {isPlaying ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                                <rect x="6" y="4" width="4" height="16" rx="1" />
                                <rect x="14" y="4" width="4" height="16" rx="1" />
                            </svg>
                        ) : (
                            <svg
                                width="20" height="20" viewBox="0 0 24 24"
                                fill="white" style={{ marginLeft: 2 }}
                            >
                                <polygon points="5 3 19 12 5 21 5 3" />
                            </svg>
                        )}
                    </button>

                    <SkipBtn onClick={() => skip(5)} label="Forward 5s">
                        <svg
                            width="16" height="16" viewBox="0 0 24 24"
                            fill="none" stroke="currentColor" strokeWidth="2"
                            strokeLinecap="round" strokeLinejoin="round"
                        >
                            <polygon points="5 4 15 12 5 20 5 4" />
                            <line x1="19" y1="5" x2="19" y2="19" />
                        </svg>
                    </SkipBtn>

                    <SkipBtn onClick={() => skip(15)} label="Forward 15s">
                        <svg
                            width="18" height="18" viewBox="0 0 24 24"
                            fill="none" stroke="currentColor" strokeWidth="2"
                            strokeLinecap="round" strokeLinejoin="round"
                        >
                            <polyline points="23 4 23 10 17 10" />
                            <path d="M20.49 15a9 9 0 1 1-.49-3.87" />
                        </svg>
                        <span className="recorder-skip-btn__label">15</span>
                    </SkipBtn>
                </div>

                {/* ── Volume ── */}
                <div className="recorder-volume-row">
                    <span className="recorder-volume-icon">{volIcon}</span>
                    <input
                        type="range"
                        min={0}
                        max={1}
                        step={0.01}
                        value={volume}
                        onChange={(e) => handleVolume(e.target.value)}
                        className="recorder-range"
                        style={{ flex: 1 }}
                        aria-label="Volume"
                    />
                    <span className="recorder-volume-pct">
                        {Math.round(volume * 100)}%
                    </span>
                </div>

                {/* ── Speed ── */}
                <div className="recorder-speed-row">
                    <span className="recorder-speed-row__label">Playback speed</span>
                    <div className="recorder-speed-row__btns">
                        {SPEEDS.map((s) => (
                            <button
                                key={s}
                                className={`recorder-speed-btn${speed === s ? " recorder-speed-btn--active" : ""}`}
                                onClick={() => handleSpeed(s)}
                                aria-pressed={speed === s}
                            >
                                {s}×
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function SkipBtn({ onClick, label, children }) {
    return (
        <button
            className="recorder-skip-btn"
            onClick={onClick}
            title={label}
            aria-label={label}
        >
            {children}
        </button>
    );
}
